import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Check, Loader2, Merge, Plus, Split, X } from "lucide-react";
import GlowCard from "../components/GlowCard";
import PdfPane from "../components/PdfPane";
import RuleCard from "./RuleCard";
import SplitMergeDialog from "./SplitMergeDialog";
import GapEvidence from "./GapEvidence";
import { mergeRules, setSectionReview, splitRule } from "./api";
import { ReviewState, RulePart, VerityRule, VerityStandard, VeritySection } from "./types";

interface SectionReviewPaneProps {
  standard: VerityStandard;
  section: VeritySection;
  sections: VeritySection[];
  review: ReviewState;
  onBack: () => void;
  onReload: () => Promise<void>;
}

/**
 * One section, reviewed against the document it came from.
 *
 * The document is on the left for the same reason it is in intake: a requirement is only checkable
 * against the sentence it was read from, and making somebody go and find that sentence is most of the
 * reason field-by-field review gets skipped. Selecting a requirement moves the document to its page
 * and marks its quote.
 */
const SectionReviewPane = ({
  standard,
  section,
  sections,
  review,
  onBack,
  onReload,
}: SectionReviewPaneProps) => {
  const [file, setFile] = useState<File | null>(null);
  /** The document is a 27 MB fetch. Until it lands the pane must say so, not say there is none. */
  const [loadingDoc, setLoadingDoc] = useState(true);
  /** Exception-based by default: the reviewer's job is the flagged few, not the verified many. */
  const [view, setView] = useState<"attention" | "all">("attention");
  const [selected, setSelected] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [dialog, setDialog] = useState<{ mode: "split" | "merge"; rules: VerityRule[] } | null>(null);
  const [focusPage, setFocusPage] = useState(section.pdfPageStart);
  const [focusQuote, setFocusQuote] = useState<string | undefined>(undefined);
  const [jumpKey, setJumpKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allRules = useMemo(
    () =>
      review.rules
        .filter((r) => r.sectionId === section.sectionId && r.decision !== "excluded")
        .sort((a, b) => a.charStart - b.charStart),
    [review.rules, section.sectionId],
  );

  const needsAttention = useMemo(
    () => allRules.filter((r) => r.validation !== "verified" && !r.decision),
    [allRules],
  );

  const rules = view === "attention" ? needsAttention : allRules;

  const sectionReview = review.sectionReviews.find((r) => r.sectionId === section.sectionId);
  const settled =
    sectionReview?.state === "reviewed" ||
    sectionReview?.state === "no-requirements" ||
    // Cleared by the machine only counts as settled if the machine actually got an answer.
    (sectionReview?.state === "machine-cleared" && sectionReview.extraction?.completed === true);
  /** A section flagged as a possible miss: obligation language, and nothing extracted. */
  const gap = sectionReview?.state === "extraction-gap";
  /**
   * A section that could not be read at all. Not a gap, and emphatically not this reviewer's problem
   * — there is nothing here to judge, because nothing was ever looked at.
   */
  const failed = sectionReview?.state === "extraction-failed";
  /**
   * A zero-rule verdict with no proof the model ever answered. Shown as what it is — unread — rather
   * than as a finding, because the run that wrote most of these was being rate-limited at the time.
   */
  const unproven =
    (sectionReview?.state === "extraction-gap" || sectionReview?.state === "machine-cleared") &&
    sectionReview?.extraction?.completed !== true;

  /**
   * The document is fetched here rather than passed down, because the reviewer arrives from the
   * library rather than from an upload — there is no File in hand, only a standard that owns one.
   */
  useEffect(() => {
    let cancelled = false;
    // Reset on every change of document, or moving to a section in another part of a split standard
    // would show the previous part's pages while the new one loaded.
    setLoadingDoc(true);
    setFile(null);
    const url = `${process.env.REACT_APP_BACKEND_URL}/verity/standards/${standard.id}/file/${section.docIndex}`;

    fetch(url, { credentials: "include" })
      .then((res) => (res.ok ? res.blob() : Promise.reject(new Error("Could not open the document"))))
      .then((blob) => {
        if (!cancelled) setFile(new File([blob], standard.fileName ?? "standard.pdf", { type: "application/pdf" }));
      })
      .catch(() => {
        if (!cancelled) setError("The source document could not be opened.");
      })
      .finally(() => {
        if (!cancelled) setLoadingDoc(false);
      });

    return () => {
      cancelled = true;
    };
  }, [standard.id, standard.fileName, section.docIndex]);

  const focus = useCallback((rule: VerityRule) => {
    setSelected(rule.ruleId);
    setFocusPage(rule.pdfPage);
    setFocusQuote(rule.sourceQuote);
    setJumpKey((k) => k + 1);
  }, []);

  const toggleChecked = (ruleId: string) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(ruleId)) next.delete(ruleId);
      else next.add(ruleId);
      return next;
    });

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      await onReload();
      setChecked(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : "That could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  const applyDialog = async (parts: RulePart[]) => {
    if (!dialog) return;
    const source = dialog;
    setDialog(null);
    await run(() =>
      source.mode === "split"
        ? splitRule(standard.id, source.rules[0]!.ruleId, parts)
        : mergeRules(standard.id, source.rules.map((r) => r.ruleId), parts[0]!),
    );
  };

  const selectedRules = rules.filter((r) => checked.has(r.ruleId));

  return (
    <>
      <GlowCard className="vr-table-card vr-review-card" hover={false}>
        <div className="vr-table-head">
          <div style={{ display: "flex", alignItems: "center", gap: ".75rem", minWidth: 0 }}>
            <button className="vr-rowbtn" onClick={onBack}>
              <ArrowLeft size={12} /> Chapters
            </button>
            <div style={{ minWidth: 0 }}>
              <h3>
                {section.numbering} {section.title}
              </h3>
              <div className="vr-cell-code">
                {section.path.slice(0, -1).join(" › ")} · printed page{" "}
                {section.printedPageStart ?? "—"} · pdf {section.pdfPageStart}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: ".5rem", alignItems: "center" }}>
            {selectedRules.length >= 2 && (
              <button
                className="vr-rowbtn"
                onClick={() => setDialog({ mode: "merge", rules: selectedRules })}
                disabled={busy}
              >
                <Merge size={12} /> Merge {selectedRules.length}
              </button>
            )}
            <button
              className="vr-btn vr-btn-outline vr-btn-sm"
              disabled={busy || allRules.length === 0 || settled}
              onClick={() =>
                void run(() => setSectionReview(standard.id, section.sectionId, "reviewed"))
              }
              title={
                needsAttention.length > 0
                  ? `${needsAttention.length} requirement${needsAttention.length === 1 ? "" : "s"} still flagged`
                  : undefined
              }
            >
              <Check size={13} /> Mark section reviewed
            </button>
          </div>
        </div>

        {error && <div className="vr-form-error" style={{ margin: "1rem 1.25rem 0" }}>{error}</div>}

        <div className="vr-split vr-review-split">
          <PdfPane
            file={file}
            loading={loadingDoc}
            page={focusPage}
            quote={focusQuote}
            jumpKey={jumpKey}
          />

          <div className="vr-review-pane">
            {settled && (
              <div className="vr-settled">
                <Check size={13} />{" "}
                {sectionReview?.state === "no-requirements"
                  ? "Confirmed by a person as containing no requirements"
                  : sectionReview?.state === "machine-cleared"
                    ? "No obligations found, and no obligation language present — cleared automatically"
                    : "Reviewed"}
                {sectionReview?.reviewedAt && (
                  <span className="vr-cell-dim">
                    {" "}
                    · {new Date(sectionReview.reviewedAt).toLocaleDateString()}
                  </span>
                )}
              </div>
            )}

            {/* The state that makes a miss visible. Zero rules because nobody looked and zero rules
                because somebody looked and found none are the same number and completely different
                facts — so one of them has to be asserted. */}
            {/* Exception-based by default. The 118 verified requirements are one click away, but a
                reviewer should not have to walk them to reach the 14 that need thinking about. */}
            {allRules.length > 0 && (
              <div className="vr-view-switch">
                <button
                  className={`vr-view-btn ${view === "attention" ? "vr-active" : ""}`}
                  onClick={() => setView("attention")}
                >
                  Needs attention ({needsAttention.length})
                </button>
                <button
                  className={`vr-view-btn ${view === "all" ? "vr-active" : ""}`}
                  onClick={() => setView("all")}
                >
                  All requirements ({allRules.length})
                </button>
              </div>
            )}

            {allRules.length > 0 && view === "attention" && needsAttention.length === 0 && (
              <div className="vr-all-clear">
                <Check size={14} />
                <div>
                  <strong>Nothing needs attention here.</strong>
                  <p>
                    All {allRules.length} requirements were quoted from the source, located on a real
                    page, and passed their automated checks. Open <em>All requirements</em> to read
                    them.
                  </p>
                </div>
              </div>
            )}

            {/* A gap Verity has analysed is a list of decisions, not a warning. The old panel told
                the reviewer that two sentences somewhere in the section contained "shall" and left
                them to find them, judge them and compose the requirement — which is the extractor's
                job done by hand. */}
            {allRules.length === 0 && gap && sectionReview?.recovery ? (
              <GapEvidence
                standardId={standard.id}
                sectionId={section.sectionId}
                recovery={sectionReview.recovery}
                obligationSignals={sectionReview.obligationSignals ?? 0}
                busy={busy}
                onFocus={(candidate) => {
                  setFocusPage(candidate.pdfPage);
                  setFocusQuote(candidate.quote);
                  setJumpKey((k) => k + 1);
                }}
                onChanged={onReload}
              />
            ) : allRules.length === 0 ? (
              <div className={`vr-no-rules ${failed ? "vr-failed" : unproven ? "" : gap ? "vr-gap" : ""}`}>
                <p className="vr-no-rules-title">
                  {failed
                    ? "This section could not be read"
                    : unproven
                      ? "Pending extraction"
                      : gap
                        ? "Possible extraction gap"
                        : "No requirements identified"}
                </p>
                <p>
                  {unproven ? (
                    <>
                      An earlier run recorded no requirements here, but did not record whether the
                      model ever answered — and the run that wrote most of these records was being
                      rate-limited at the time. So this is not a finding about the section, and there
                      is nothing here for you to judge. Resume processing and it will be read
                      properly.
                    </>
                  ) : failed ? (
                    <>
                      The extraction call failed and nothing was read from this section — so nothing
                      here is a judgement about its contents, and there is nothing for you to confirm.
                      Run the standard again and this section will be read.
                      {sectionReview?.note && (
                        <>
                          {" "}
                          <span className="vr-cell-code">{sectionReview.note}</span>
                        </>
                      )}
                    </>
                  ) : gap ? (
                    <>
                      This section uses obligation language — {sectionReview?.obligationSignals ?? 0}{" "}
                      sentence{(sectionReview?.obligationSignals ?? 0) === 1 ? "" : "s"} containing
                      "shall", "must" or "required" — but nothing was extracted from it. That is a
                      miss until somebody reads it and says otherwise.
                    </>
                  ) : (
                    <>
                      No obligations were found, and the section contains no obligation language
                      either. It has been cleared automatically — confirm if you want it on the
                      record as read.
                    </>
                  )}
                </p>
                {/* No confirm button on a failed or unproven section. "Confirmed — no requirements"
                    would freeze a claim nobody is in a position to make, over a section nothing ever
                    successfully looked at. */}
                {!failed && !unproven && (
                  <div className="vr-no-rules-actions">
                    <button
                      className="vr-btn vr-btn-outline vr-btn-sm"
                      disabled={busy || settled}
                      onClick={() =>
                        void run(() =>
                          setSectionReview(standard.id, section.sectionId, "no-requirements"),
                        )
                      }
                    >
                      <Check size={13} /> Confirm — no requirements
                    </button>
                    <button className="vr-btn vr-btn-ghost vr-btn-sm" disabled title="Coming with the editor">
                      <Plus size={13} /> Add requirement
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="vr-review-count">
                  {rules.length} requirement{rules.length === 1 ? "" : "s"}
                  {view === "all" && needsAttention.length > 0 && (
                    <> · {needsAttention.length} needing attention</>
                  )}
                </div>

                {rules.map((rule) => (
                  <RuleCard
                    key={rule.ruleId}
                    rule={rule}
                    allRules={review.rules}
                    standardId={standard.id}
                    selected={selected === rule.ruleId}
                    checked={checked.has(rule.ruleId)}
                    busy={busy}
                    onFocus={() => focus(rule)}
                    onToggleChecked={() => toggleChecked(rule.ruleId)}
                    onSplit={() => setDialog({ mode: "split", rules: [rule] })}
                    onChanged={onReload}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      </GlowCard>

      {dialog && (
        <SplitMergeDialog
          mode={dialog.mode}
          rules={dialog.rules}
          onCancel={() => setDialog(null)}
          onApply={applyDialog}
        />
      )}

      {busy && (
        <div className="vr-busy vr-busy-fixed">
          <div className="vr-busy-card">
            <Loader2 size={20} className="vr-spin-inline" />
            <p className="vr-busy-title">Saving</p>
          </div>
        </div>
      )}
    </>
  );
};

export default SectionReviewPane;
